import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { sortPeople } from '../../../utils/peopleSort'

interface User { id: number; full_name: string }
interface Comment { id: number; body: string; user: User; parent_comment_id: number | null; created_at: string }
interface ToDo {
  id: number; title: string; description: string | null;
  priority: string; due_date: string | null; to_do_list_id: number;
  owner: User | null; completed: boolean;
  checklist: Array<{ id?: string; title: string; done: boolean }>;
  comments: Comment[];
  linked_action_item_id: number | null;
  linked_topic_id: number | null;
}

interface Props {
  project: { id: number }
  toDo: ToDo
  orgMembers: User[]
  orgActionItems?: Array<{ id: number; title: string; owner_id: number }>
  canEdit: boolean
  onClose: () => void
}

export default function ToDoDetail({ project, toDo, orgMembers, orgActionItems = [], canEdit, onClose }: Props) {
  const { data, setData, put, processing } = useForm({
    to_do: {
      title: toDo.title,
      description: toDo.description || '',
      owner_id: toDo.owner?.id || '',
      priority: toDo.priority,
      due_date: toDo.due_date || ''
    }
  })
  const [commentBody, setCommentBody] = useState('')

  function save() {
    put(`/projects/${project.id}/to_dos/${toDo.id}`, { preserveScroll: true })
  }
  function toggleComplete() {
    router.post(`/projects/${project.id}/to_dos/${toDo.id}/${toDo.completed ? 'reopen' : 'complete'}`, {}, { preserveScroll: true })
  }
  function destroy() {
    if (!confirm('Delete this to-do?')) return
    router.delete(`/projects/${project.id}/to_dos/${toDo.id}`)
  }
  function postComment(e: React.FormEvent) {
    e.preventDefault()
    if (!commentBody.trim()) return
    router.post(`/projects/${project.id}/to_dos/${toDo.id}/comments`, { body: commentBody }, { preserveScroll: true, onSuccess: () => setCommentBody('') })
  }
  function toggleChecklistItem(idx: number) {
    const list = [...toDo.checklist]
    list[idx] = { ...list[idx], done: !list[idx].done }
    router.put(`/projects/${project.id}/to_dos/${toDo.id}`, { to_do: { ...data.to_do, checklist: list } }, { preserveScroll: true })
  }
  function addChecklistItem(title: string) {
    if (!title.trim()) return
    const list = [...toDo.checklist, { title, done: false }]
    router.put(`/projects/${project.id}/to_dos/${toDo.id}`, { to_do: { ...data.to_do, checklist: list } }, { preserveScroll: true })
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-white w-[600px] max-w-full h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-start justify-between">
          <input
            type="text"
            value={data.to_do.title}
            disabled={!canEdit}
            onChange={(e) => setData('to_do', { ...data.to_do, title: e.target.value })}
            onBlur={save}
            className="flex-1 text-lg font-semibold text-text-primary bg-transparent border-none outline-none"
          />
          <button onClick={onClose} className="text-2xl text-text-muted ml-3">×</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <button onClick={toggleComplete} disabled={!canEdit} className={`px-3 py-1.5 text-xs rounded-lg ${toDo.completed ? 'bg-[#15532E] text-white' : 'border'}`}>
              {toDo.completed ? '✓ Completed' : 'Mark complete'}
            </button>
            {canEdit && <button onClick={destroy} className="text-xs text-[#ED1C24]">Delete</button>}
          </div>

          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <label className="block text-xs text-text-muted mb-1">Owner</label>
              <select disabled={!canEdit} value={data.to_do.owner_id} onChange={(e) => setData('to_do', { ...data.to_do, owner_id: e.target.value ? Number(e.target.value) : '' })} onBlur={save} className="w-full border rounded px-2 py-1 text-sm">
                <option value="">—</option>
                {sortPeople(orgMembers).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Priority</label>
              <select disabled={!canEdit} value={data.to_do.priority} onChange={(e) => setData('to_do', { ...data.to_do, priority: e.target.value })} onBlur={save} className="w-full border rounded px-2 py-1 text-sm">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Due date</label>
              <input type="date" disabled={!canEdit} value={data.to_do.due_date} onChange={(e) => setData('to_do', { ...data.to_do, due_date: e.target.value })} onBlur={save} className="w-full border rounded px-2 py-1 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1">Link to Action Item</label>
            <select
              disabled={!canEdit}
              defaultValue={toDo.linked_action_item_id || ''}
              onChange={(e) => router.put(`/projects/${project.id}/to_dos/${toDo.id}`, { to_do: { ...data.to_do, linked_action_item_id: e.target.value ? Number(e.target.value) : null } }, { preserveScroll: true })}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              <option value="">None — completing this to-do will not affect any action item</option>
              {orgActionItems.map((ai) => <option key={ai.id} value={ai.id}>{ai.title}</option>)}
            </select>
            <p className="text-xs text-text-muted mt-1">Completing this to-do (or moving it into a Done list) will mark the linked action item complete.</p>
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1">Description (markdown)</label>
            <textarea rows={4} disabled={!canEdit} value={data.to_do.description} onChange={(e) => setData('to_do', { ...data.to_do, description: e.target.value })} onBlur={save} className="w-full border rounded px-2 py-2 text-sm" />
          </div>

          <div>
            <h3 className="font-semibold text-sm text-text-primary mb-2">Checklist</h3>
            <ul className="space-y-1">
              {toDo.checklist.map((c, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" disabled={!canEdit} checked={!!c.done} onChange={() => toggleChecklistItem(i)} />
                  <span className={c.done ? 'line-through text-text-muted' : 'text-text-primary'}>{c.title}</span>
                </li>
              ))}
            </ul>
            {canEdit && (
              <form onSubmit={(e) => { e.preventDefault(); const f = e.currentTarget; const v = (f.elements.namedItem('item') as HTMLInputElement).value; addChecklistItem(v); f.reset() }} className="mt-2">
                <input name="item" placeholder="+ Add checklist item" className="text-xs border rounded px-2 py-1 w-full" />
              </form>
            )}
          </div>

          <div>
            <h3 className="font-semibold text-sm text-text-primary mb-2">Comments</h3>
            <ul className="space-y-2 mb-3">
              {toDo.comments.map((c) => (
                <li key={c.id} className="text-sm">
                  <div className="text-xs text-text-muted">{c.user.full_name} · {new Date(c.created_at).toLocaleString()}</div>
                  <p className="text-text-primary whitespace-pre-wrap">{c.body}</p>
                </li>
              ))}
            </ul>
            {canEdit && (
              <form onSubmit={postComment}>
                <textarea rows={2} value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Add a comment…" className="w-full border rounded px-2 py-1 text-sm" />
                <button type="submit" className="mt-2 px-3 py-1 text-xs text-white rounded" style={{ backgroundColor: '#ED1C24' }}>Post comment</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
